import { useAuth } from '@wui/common-library'
import {
  isCallStarted,
  MessageCallReceived,
  MessageIncomingEvent,
  PartialHistoryMessageItem,
  ReadyEvent,
  useIM,
} from '@wui/im'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken } from 'firebase/messaging'
import { storeToRefs } from 'pinia'
import { computed, Plugin, watch } from 'vue'

import { name } from '@/../package.json'

import { useAuthApiTokenValidator } from '@/shared/composables/auth'
import { isWebNotificationApiAvailable } from '@/shared/composables/browser-api'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useUserData } from '@/shared/composables/local-storage'
import { useNotification } from '@/shared/composables/use-notification'
import { emitter } from '@/shared/services/emitter'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { useIMStore } from '@/shared/store/im'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { UserRoles } from '@/shared/types/router'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'
import { isStageOrDevelopment } from '@/shared/utils/import-meta'
import { getPlatform } from '@/shared/utils/platform'
import { fromPServedUserIfRoom } from '@/shared/utils/uri'
import { getAbsoluteWebSocketURL } from '@/shared/utils/url'

export const imPlugin: Plugin = {
  install: async () => {
    const { currentUser } = useCurrentUser()
    const { token } = useAuth()
    const { subscribe, reset, forceHandleTick } = useStatusSubscribe()
    const { init, debug, onReady, onMessage, close } = useIM()
    const { userRole } = useUserData()
    const imStore = useIMStore()
    const { setConnectionStatus, setAuthorized } = imStore
    const { connectionStatus, isAuthorized } = storeToRefs(imStore)
    let deviceToken: string | undefined
    let platform: string | undefined

    if (isStageOrDevelopment()) {
      debug.enable(undefined)
    } else {
      debug.disable()
    }
    const startIM = async (): Promise<void> => {
      if (connectionStatus.value === 'connecting' || connectionStatus.value === 'connected') {
        // Prevent multiple simultaneous connections
        return
      }
      if (!deviceToken) {
        deviceToken = await initDeviceToken()
      }
      setConnectionStatus('connecting')
      try {
        const imWs = getAbsoluteWebSocketURL('/im')
        platform = await getPlatform()
        const started = await init(imWs, () => ({
          accessToken: token.value!,
          deviceToken: deviceToken!,
          login: currentUser.value!.imLogin,
          terminalId: 10,
          productName: name,
          userAgent: platform!,
        }))
        forceHandleTick()
        if (currentUser.value?.imLogin) {
          subscribe(currentUser.value.imLogin)
        }
        setConnectionStatus(started ? 'connected' : 'disconnected')
      } catch {
        setConnectionStatus('error')
      }
    }

    const stopIM = async (): Promise<void> => {
      if (connectionStatus.value === 'disconnecting' || connectionStatus.value === 'disconnected') {
        // Prevent multiple simultaneous disconnects
        return
      }
      setConnectionStatus('disconnecting')
      try {
        await close()
      } catch {
      } finally {
        setAuthorized(false)
        reset()
        setConnectionStatus('disconnected')
      }
    }

    const { isValid: tokenIsValid } = useAuthApiTokenValidator()

    const userIsValid = computed(() => !!(
      tokenIsValid.value &&
      currentUser.value?.imLogin &&
      (userRole.value && userRole.value !== UserRoles.SUPPORT)
    ))

    watch([userIsValid, token], async ([newUserIsValid, newToken], [oldUserIsValid, oldToken]) => {
      const notConnectedYet = (connectionStatus.value === 'disconnected' || connectionStatus.value === 'error')
      const connectionStarted = (connectionStatus.value === 'connected' || connectionStatus.value === 'connecting')
      const tokenUpdated = newToken !== oldToken
      const userJustBecameValid = newUserIsValid && !oldUserIsValid
      if (tokenUpdated && !newToken) {
        await stopIM()
      } else if (tokenUpdated && newUserIsValid) {
        // Токен обновлен
        if (notConnectedYet) {
          // Подключения не было, выполняем
          await startIM()
        } else if (connectionStatus.value === 'connecting') {
          // Была попытка подключения, повторяем
          await stopIM()
          await startIM()
        }
      } else if (notConnectedYet && !newUserIsValid) {
        // Пользователь не может начинать подключение
      } else if (notConnectedYet && newUserIsValid && (tokenUpdated || userJustBecameValid)) {
        // Первое подключение после логина или появления пользователя — один вызов startIM().
        // При обрыве соединения переподключение полностью отдано библиотеке @wui/im.
        await startIM()
      } else if (connectionStarted && newUserIsValid && !isAuthorized.value) {
        // Есть активное подключение, но команда setup не была завершена успешно, переподключиться
        await stopIM()
        await startIM()
      } else if (connectionStarted && !newUserIsValid) {
        // Есть активное подключение, но пользователь не может его больше использовать
        // может быть проблема после выхода из сна (проверить)
        await stopIM()
      } else if (connectionStarted && !isAuthorized.value) {
        await stopIM()
        await startIM()
      } else {
        // все окей, ничего не делать
      }
    }, {
      immediate: true,
      flush: 'post',
    })

    onReady((event: ReadyEvent) => {
      setAuthorized(event.isReady)
      if (!event.isReady) {
        setConnectionStatus('disconnected')
      }
    })

    onMessage((event: MessageIncomingEvent | MessageCallReceived) => {
      const { createIncomingMessageNotification, createConferenceStartedNotification, closeConferenceStartedNotification } = useNotification()
      emitter.emit(EmitterEvents.NEW_MESSAGE, event)
      const wrapped: PartialHistoryMessageItem<MessageIncomingEvent | MessageCallReceived> = { item: event }
      const { pServed } = event
      if (event.type === 'IncomingMessageEvent') {
        if (event.executor !== currentUser.value?.imLogin) {
          const roomId = fromPServedUserIfRoom(pServed)
          createIncomingMessageNotification(
            pServed,
            roomId,
            event as MessageIncomingEvent,
          )
        }
      } else if (isCallStarted(wrapped)) {
        createConferenceStartedNotification(
          pServed,
          wrapped.item.roomName,
        )
      } else if (event.event && event.event === 'appointment_end') { // TODO replace to predicate isCallEnded from IM
        closeConferenceStartedNotification(pServed)
      }
    })
  },
}

const initDeviceToken = async (): Promise<string | undefined> => {
  if (!isWebNotificationApiAvailable()) {
    return undefined
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return undefined
    }

    const envSettings = useStoreEnvSettings()
    await envSettings.onInitialized()
    const pushConfig = JSON.parse(envSettings.getSettingByKey('pushConfig')?.value ?? '{}')
    const vapidKey = envSettings.getSettingByKey('vapidKey')?.value
    initializeApp(pushConfig)
    const messaging = getMessaging()
    const serviceWorkerRegistration = await navigator.serviceWorker.ready
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    })
    await useFetchApi().post(getAppURL({ pathName: '/api/user-device-token' }), { token, enabled: true, additionalData: 'web-client' })
    return token
  } catch {
    return undefined
  }
}
